import React, { useEffect, useState } from 'react';
import { Alert, Badge, Button, Form, ListGroup, Spinner } from 'react-bootstrap';
import { Link, useSearchParams } from 'react-router-dom';
import bakingApi from '../services/bakingApi';

function EvidenceChips({ citations, evidence }) {
  if (!citations || citations.length === 0) return null;
  const byKey = {};
  (evidence || []).forEach((e) => {
    byKey[e.citation_key] = e;
  });
  return (
    <div className="mt-2 d-flex flex-wrap gap-1">
      {citations.map((key) => {
        const item = byKey[key];
        return (
          <Badge bg="secondary" key={key} title={item ? item.display_label : key}>
            {key}
            {item ? ` · ${item.evidence_type}` : ''}
          </Badge>
        );
      })}
    </div>
  );
}

function severityVariant(severity) {
  if (severity === 'critical' || severity === 'error') return 'danger';
  if (severity === 'warning') return 'warning';
  return 'secondary';
}

function RecommendationCard({ rec, onStatus }) {
  return (
    <div className="border rounded p-2 mb-2" data-testid="ai-recommendation-card">
      <div className="d-flex flex-wrap gap-1 align-items-center mb-1">
        <Badge bg={severityVariant(rec.severity)}>{rec.severity}</Badge>
        <Badge bg="light" text="dark">
          {rec.category}
        </Badge>
        <Badge bg="info">{rec.confidence}</Badge>
        <Badge bg="dark">{rec.status}</Badge>
        {rec.freshness && rec.freshness !== 'current' && (
          <Badge bg="warning" text="dark">
            {rec.freshness}
          </Badge>
        )}
      </div>
      <strong>{rec.title}</strong>
      <p className="mb-1 small text-muted">{rec.summary}</p>
      <p className="mb-1 small">{rec.recommended_action}</p>
      {rec.expected_outcome && (
        <p className="mb-1 small text-muted">Expected: {rec.expected_outcome}</p>
      )}
      <EvidenceChips citations={rec.citation_keys} evidence={rec.evidence} />
      <div className="mt-2 d-flex flex-wrap gap-1">
        {rec.status === 'open' && (
          <>
            <Button size="sm" variant="outline-secondary" onClick={() => onStatus('review', rec.id)}>
              Mark reviewed
            </Button>
            <Button size="sm" variant="outline-primary" onClick={() => onStatus('accept', rec.id)}>
              Accept as advice
            </Button>
            <Button size="sm" variant="outline-secondary" onClick={() => onStatus('dismiss', rec.id)}>
              Dismiss
            </Button>
            <Button size="sm" variant="outline-success" onClick={() => onStatus('resolve', rec.id)}>
              Mark resolved
            </Button>
          </>
        )}
        {(rec.status === 'dismissed' || rec.status === 'resolved' || rec.status === 'reviewed' || rec.status === 'accepted_as_advice') && (
          <Button size="sm" variant="outline-secondary" onClick={() => onStatus('reopen', rec.id)}>
            Reopen
          </Button>
        )}
      </div>
    </div>
  );
}

function TechnicalArtistPage() {
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [evidence, setEvidence] = useState([]);
  const [recommendations, setRecommendations] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [activeConversation, setActiveConversation] = useState(null);

  const loadRecommendations = async (conversation) => {
    const params = {};
    if (conversation?.project) params.project = conversation.project;
    if (conversation?.revision) params.revision = conversation.revision;
    if (conversation?.job) params.job = conversation.job;
    if (searchParams.get('project')) params.project = searchParams.get('project');
    if (searchParams.get('revision')) params.revision = searchParams.get('revision');
    if (searchParams.get('job')) params.job = searchParams.get('job');
    try {
      const data = await bakingApi.listAiRecommendations(params);
      setRecommendations(Array.isArray(data) ? data : []);
    } catch (err) {
      setRecommendations([]);
    }
  };

  const loadConversations = async () => {
    const data = await bakingApi.listAiConversations();
    setConversations(data);
    return data;
  };

  const loadMessages = async (conversationId) => {
    const data = await bakingApi.listAiMessages(conversationId);
    setMessages(data);
    setEvidence([]);
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const projectId = searchParams.get('project');
        const revisionId = searchParams.get('revision');
        const jobId = searchParams.get('job');
        let data = await loadConversations();
        if ((projectId || revisionId || jobId) && data.length === 0) {
          const created = await bakingApi.createAiConversation({
            project_id: projectId || undefined,
            revision_id: revisionId || undefined,
            job_id: jobId || undefined,
            title: 'Technical Artist',
          });
          data = await loadConversations();
          if (!cancelled) {
            setActiveId(created.id);
            setActiveConversation(created);
            await loadMessages(created.id);
            await loadRecommendations(created);
          }
        } else if (data[0] && !cancelled) {
          setActiveId(data[0].id);
          setActiveConversation(data[0]);
          await loadMessages(data[0].id);
          await loadRecommendations(data[0]);
        } else if (!cancelled) {
          await loadRecommendations(null);
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load AI Technical Artist');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSelect = async (id) => {
    setActiveId(id);
    const conv = conversations.find((c) => c.id === id) || null;
    setActiveConversation(conv);
    setError(null);
    try {
      await loadMessages(id);
      await loadRecommendations(conv);
    } catch (err) {
      setError(err.message || 'Failed to load messages');
    }
  };

  const onCreate = async () => {
    setError(null);
    try {
      const created = await bakingApi.createAiConversation({
        title: 'New Technical Artist chat',
        project_id: searchParams.get('project') || undefined,
        revision_id: searchParams.get('revision') || undefined,
        job_id: searchParams.get('job') || undefined,
      });
      await loadConversations();
      setActiveId(created.id);
      setActiveConversation(created);
      setMessages([]);
      setEvidence([]);
      await loadRecommendations(created);
    } catch (err) {
      setError(err.message || 'Failed to create conversation');
    }
  };

  const onSend = async (event) => {
    event.preventDefault();
    if (!activeId || !input.trim()) return;
    setSending(true);
    setError(null);
    try {
      const result = await bakingApi.postAiMessage(activeId, { content: input.trim() });
      setInput('');
      await loadMessages(activeId);
      await loadRecommendations(activeConversation);
      if (result.run && result.run.id) {
        try {
          const ev = await bakingApi.listAiRunEvidence(result.run.id);
          setEvidence(ev);
        } catch (err) {
          setEvidence([]);
        }
      }
    } catch (err) {
      setError(err.message || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const onStatus = async (action, id) => {
    setError(null);
    try {
      if (action === 'review') await bakingApi.reviewAiRecommendation(id);
      if (action === 'accept') await bakingApi.acceptAiRecommendation(id);
      if (action === 'dismiss') await bakingApi.dismissAiRecommendation(id);
      if (action === 'resolve') await bakingApi.resolveAiRecommendation(id);
      if (action === 'reopen') await bakingApi.reopenAiRecommendation(id);
      await loadRecommendations(activeConversation);
    } catch (err) {
      setError(err.message || 'Failed to update recommendation');
    }
  };

  if (loading) {
    return (
      <div className="loading-state">
        <Spinner animation="border" size="sm" className="me-2" />
        Loading AI Technical Artist…
      </div>
    );
  }

  return (
    <div>
      <h1 className="product-page-title">AI Technical Artist</h1>
      <p className="product-page-subtitle">
        Evidence-grounded diagnostics and advisory recommendations. Status controls manage
        recommendation records only — no project changes or job execution occur.
      </p>
      <Alert variant="info" className="border">
        Stage 2 is advisory only. Accepting a recommendation does not apply a fix, create a
        job, or execute Unity/Maya.
      </Alert>
      {error && <div className="error-state mb-3">{error}</div>}

      <div className="row g-3">
        <div className="col-md-3">
          <div className="product-card">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="h6 mb-0">Conversations</h2>
              <Button size="sm" variant="outline-primary" onClick={onCreate}>
                New
              </Button>
            </div>
            {conversations.length === 0 ? (
              <p className="text-muted mb-0">No conversations yet.</p>
            ) : (
              <ListGroup>
                {conversations.map((c) => (
                  <ListGroup.Item
                    key={c.id}
                    action
                    active={c.id === activeId}
                    onClick={() => onSelect(c.id)}
                  >
                    {c.title || 'Untitled'}
                  </ListGroup.Item>
                ))}
              </ListGroup>
            )}
          </div>
        </div>
        <div className="col-md-5">
          <div className="product-card" data-testid="ai-chat-panel">
            {activeConversation && (
              <p className="text-muted small">
                Context:{' '}
                {activeConversation.project ? (
                  <Link to={`/console/projects/${activeConversation.project}`}>Project</Link>
                ) : (
                  'Partner'
                )}
                {activeConversation.revision ? ' · revision anchored' : ''}
                {activeConversation.job ? ' · job anchored' : ''}
              </p>
            )}
            <div className="mb-3" style={{ minHeight: 240 }}>
              {messages.length === 0 ? (
                <p className="text-muted">Ask about a revision, compatibility result, or job.</p>
              ) : (
                messages.map((m) => (
                  <div key={m.id} className="mb-3" data-testid={`ai-message-${m.role}`}>
                    <strong>{m.role === 'user' ? 'You' : 'AI Technical Artist'}</strong>
                    <div>{m.content}</div>
                    {m.role === 'assistant' && (
                      <EvidenceChips
                        citations={m.citation_keys}
                        evidence={evidence}
                      />
                    )}
                  </div>
                ))
              )}
            </div>
            <Form onSubmit={onSend}>
              <Form.Control
                as="textarea"
                rows={3}
                value={input}
                disabled={!activeId || sending}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask the AI Technical Artist…"
                data-testid="ai-chat-input"
              />
              <div className="mt-2 d-flex justify-content-end">
                <Button type="submit" disabled={!activeId || sending || !input.trim()}>
                  {sending ? 'Thinking…' : 'Send'}
                </Button>
              </div>
            </Form>
          </div>
        </div>
        <div className="col-md-4">
          <div className="product-card" data-testid="ai-recommendations-panel">
            <h2 className="h6">Recommendations</h2>
            <p className="small text-muted">
              Advisory records only. No Apply, Run, Fix, or Execute actions.
            </p>
            {recommendations.length === 0 ? (
              <p className="text-muted mb-0">No recommendations for this context yet.</p>
            ) : (
              recommendations.map((rec) => (
                <RecommendationCard key={rec.id} rec={rec} onStatus={onStatus} />
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default TechnicalArtistPage;
